import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Check, Loader2, Leaf } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SPECIALIZATIONS = ["Carbon Accounting", "ESG Reporting", "Climate Risk", "Supply Chain", "Net Zero Strategy", "Governance"];

export default function Onboarding() {
  const { user, checking, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [specs, setSpecs] = useState([]);
  const [certs, setCerts] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!checking && !user) nav("/login");
    if (user?.onboarded) nav("/");
  }, [user, checking, nav]);

  useEffect(() => { api.get("/auth/ethics-code").then(({ data }) => setCode(data)).catch(() => {}); }, []);

  const toggleSpec = (s) => setSpecs((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/onboard", {
        code_version: code?.version || "1.0", accepted: true,
        specializations: specs, certifications: certs.split(",").map((c) => c.trim()).filter(Boolean), bio,
      });
      setUser(data);
      toast.success("Onboarding complete. Welcome to your practice.");
      nav("/");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center"><Leaf className="w-5 h-5 text-white" /></div>
        <div className="font-display font-bold text-slate-900">SusTek Adviser OS</div>
      </div>

      <div className="flex-1 flex items-start justify-center py-10 px-4">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
          {/* progress */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                <div className={`h-1 flex-1 rounded-full ${step > s ? "bg-emerald-600" : "bg-slate-200"}`} />
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
              <div className="flex items-center gap-2 text-emerald-700 mb-1"><ShieldCheck className="w-5 h-5" /><span className="eyebrow text-emerald-700">Step 1 · Ethics attestation</span></div>
              <h1 className="font-display text-2xl font-bold text-slate-900">{code?.title || "Code of Professional Conduct"}</h1>
              <p className="text-sm text-slate-500 mt-1">Version {code?.version || "1.0"} — you must accept this to use the platform.</p>
              <ul className="mt-6 space-y-3">
                {(code?.clauses || []).map((c, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" /> {c}
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-3 mt-8 p-4 rounded-xl bg-emerald-50 border border-emerald-200 cursor-pointer">
                <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="ethics-accept-checkbox" />
                <span className="text-sm font-medium text-slate-800">I have read and accept the Code of Professional Conduct.</span>
              </label>
              <div className="flex justify-end mt-6">
                <Button disabled={!accepted} onClick={() => setStep(2)} data-testid="ethics-continue" className="bg-emerald-600 hover:bg-emerald-700">Continue</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
              <span className="eyebrow">Step 2 · Your profile</span>
              <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">Tell us about your practice</h1>
              <p className="text-sm text-slate-500 mt-1">This personalises your Control Tower and recommendations.</p>

              <div className="mt-6">
                <label className="text-sm font-medium text-slate-700">Specializations</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SPECIALIZATIONS.map((s) => (
                    <button key={s} type="button" onClick={() => toggleSpec(s)} data-testid={`spec-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${specs.includes(s) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <label className="text-sm font-medium text-slate-700">Certifications <span className="text-slate-400">(comma separated)</span></label>
                <Input value={certs} onChange={(e) => setCerts(e.target.value)} data-testid="certs-input" className="mt-1.5" placeholder="GRI Certified, SBTi Practitioner" />
              </div>
              <div className="mt-5">
                <label className="text-sm font-medium text-slate-700">Short bio</label>
                <Textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="bio-input" className="mt-1.5" placeholder="Independent sustainability adviser focused on…" />
              </div>
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={finish} disabled={saving} data-testid="finish-onboarding" className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter my practice"}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
